/**
 * Loan-purpose selector (WI-TV-W6-001).
 *
 * The entry landing for the two Wave 6 step journeys, and the destination of the
 * shared pre-submit `Change loan type` action. It is not a numbered input step,
 * so progress semantics are unchanged, and it offers exactly the two Wave 6
 * purposes — no Home Equity option and no Loan Officer flow. Both choices are
 * ordinary buttons, so they are reachable and activatable by keyboard with no
 * extra key handling, and the heading takes focus when the selector is shown
 * after a reset so assistive tech announces where the borrower landed.
 */
import logoUrl from "../assets/loandepot-logo.svg";

export const LOAN_PURPOSES = ["purchase", "refinance"] as const;

export type LoanPurpose = (typeof LOAN_PURPOSES)[number];

const PURPOSE_LABELS: Readonly<Record<LoanPurpose, string>> = {
  purchase: "Buying a home",
  refinance: "Refinancing my home",
};

export interface LoanPurposeSelectorOptions {
  onSelect: (purpose: LoanPurpose) => void;
  /**
   * True when the selector is being shown because the borrower chose to change
   * loan type, so it states plainly that the previous answers were cleared.
   */
  afterReset?: boolean;
}

export function renderLoanPurposeSelector(
  root: HTMLElement,
  options: LoanPurposeSelectorOptions,
): void {
  root.innerHTML = `
    <div class="wizard" data-wizard data-loan-purpose-selector>
      <header class="wizard-header">
        <img class="wizard-logo" src="${logoUrl}" alt="loanDepot" width="172" height="34" />
        <p class="wizard-header-sub">Get started</p>
      </header>
      <main class="wizard-surface">
        <div class="wizard-card" role="group" aria-labelledby="loan-purpose-title">
          <h1 class="wizard-step-title" id="loan-purpose-title" tabindex="-1">
            What can we help you with?
          </h1>
          ${
            options.afterReset === true
              ? `<p class="wizard-step-desc" data-reset-notice role="status">
            Your previous answers were cleared. Choose a loan type to start over.
          </p>`
              : `<p class="wizard-step-desc">
            Choose a loan type to begin. You can change it later, which starts your application
            over.
          </p>`
          }
          <div class="wizard-choice-group wizard-purpose-choices">
            ${LOAN_PURPOSES.map(
              (purpose) => `
            <button
              type="button"
              class="wizard-btn wizard-btn-primary wizard-purpose-btn"
              data-loan-purpose="${purpose}"
            >
              ${PURPOSE_LABELS[purpose]}
            </button>`,
            ).join("")}
          </div>
        </div>
      </main>
    </div>`;

  root.querySelectorAll<HTMLButtonElement>("[data-loan-purpose]").forEach((button) => {
    button.addEventListener("click", () => {
      const purpose = button.dataset.loanPurpose;
      if (purpose === "purchase" || purpose === "refinance") options.onSelect(purpose);
    });
  });

  root.querySelector<HTMLElement>("#loan-purpose-title")?.focus();
}
